import React, { useState, useCallback, useRef, useId } from "react";
import classNames from "classnames";
import type { TabsProps } from "./types";
import styles from "./Tabs.module.css";

/**
 * Tabs component - Accessible tabbed interface
 *
 * WCAG Compliant:
 * - Proper ARIA roles (tablist, tab, tabpanel)
 * - Keyboard navigation (Arrow keys, Home, End)
 * - aria-selected and aria-controls for screen readers
 */
export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onTabChange,
  className,
  "data-testid": testId,
}) => {
  const generatedId = useId();
  const tabsId = testId || `tabs-${generatedId}`;

  // Internal state for uncontrolled mode
  const [internalActiveTab, setInternalActiveTab] = useState(
    defaultTab || tabs[0]?.id || "",
  );

  // Use controlled or uncontrolled state
  const activeTab = controlledActiveTab ?? internalActiveTab;

  // Refs for keyboard navigation
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Handle tab click
  const handleTabClick = useCallback(
    (tabId: string) => {
      if (!controlledActiveTab) {
        setInternalActiveTab(tabId);
      }
      onTabChange?.(tabId);
    },
    [controlledActiveTab, onTabChange],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const enabledTabs = tabs.filter((t) => !t.disabled);
      const currentEnabledIndex = enabledTabs.findIndex(
        (t) => t.id === tabs[currentIndex].id,
      );

      let nextIndex = -1;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          nextIndex =
            currentEnabledIndex > 0
              ? currentEnabledIndex - 1
              : enabledTabs.length - 1;
          break;
        case "ArrowRight":
          e.preventDefault();
          nextIndex =
            currentEnabledIndex < enabledTabs.length - 1
              ? currentEnabledIndex + 1
              : 0;
          break;
        case "Home":
          e.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          e.preventDefault();
          nextIndex = enabledTabs.length - 1;
          break;
        default:
          return;
      }

      if (nextIndex >= 0) {
        const nextTab = enabledTabs[nextIndex];
        const nextTabRef = tabRefs.current.get(nextTab.id);
        nextTabRef?.focus();
        handleTabClick(nextTab.id);
      }
    },
    [tabs, handleTabClick],
  );

  return (
    <div className={classNames(styles.tabs, className)} data-testid={testId}>
      {/* Tab List */}
      <div
        role="tablist"
        aria-label="Tabs"
        className={styles.tabList}
        data-testid={`${tabsId}-tablist`}
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.id, el);
            }}
            role="tab"
            id={`${tabsId}-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`${tabsId}-panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            disabled={tab.disabled}
            className={styles.tab}
            onClick={() => handleTabClick(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            data-testid={`${tabsId}-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Panels */}
      {tabs.map((tab) => (
        <div
          key={tab.id}
          role="tabpanel"
          id={`${tabsId}-panel-${tab.id}`}
          aria-labelledby={`${tabsId}-tab-${tab.id}`}
          hidden={activeTab !== tab.id}
          className={styles.tabPanel}
          tabIndex={0}
          data-testid={`${tabsId}-panel-${tab.id}`}
        >
          {activeTab === tab.id && tab.content}
        </div>
      ))}
    </div>
  );
};

Tabs.displayName = "Tabs";

export default Tabs;
